import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";

const HeroSection = () => {
  const { t } = useTranslation();
  const shouldReduceMotion = useReducedMotion();
  const [activeStage, setActiveStage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Sequential stage animation
  useEffect(() => {
    if (shouldReduceMotion) return;

    const animatePipeline = () => {
      setIsAnimating(true);
      setActiveStage(0);

      const stages = [0, 1, 2, 3];
      let currentStage = 0;

      const nextStage = () => {
        if (currentStage < stages.length - 1) {
          currentStage++;
          setActiveStage(currentStage);
          setTimeout(nextStage, 1200); // 1.2s per stage
        } else {
          // Reset after completion
          setTimeout(() => {
            setIsAnimating(false);
            setTimeout(animatePipeline, 2000); // 2s pause before restart
          }, 1000);
        }
      };

      setTimeout(nextStage, 1200); // Start first transition after 1.2s
    };

    // Start the animation after component mount
    const timer = setTimeout(animatePipeline, 2000);
    return () => clearTimeout(timer);
  }, [shouldReduceMotion]);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-primary via-secondary to-primary overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-10">
        {/* Floating geometric shapes */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-16 h-16 md:w-24 md:h-24 lg:w-32 lg:h-32 bg-accent/20 rounded-full"
          animate={
            shouldReduceMotion
              ? {}
              : {
                  y: [0, -20, 0],
                  rotate: [0, 180, 360],
                  scale: [1, 1.1, 1],
                }
          }
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute top-1/3 right-1/3 w-12 h-12 md:w-18 md:h-18 lg:w-24 lg:h-24 bg-accent/15 rounded-lg"
          animate={
            shouldReduceMotion
              ? {}
              : {
                  y: [0, 15, 0],
                  rotate: [0, -90, 0],
                  scale: [1, 0.9, 1],
                }
          }
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-10 h-10 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-accent/10 rounded-full"
          animate={
            shouldReduceMotion
              ? {}
              : {
                  y: [0, -10, 0],
                  x: [0, 10, 0],
                  scale: [1, 1.2, 1],
                }
          }
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-32 pb-12 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left order-2 lg:order-1"
          >
            <div className="mb-4 md:mb-6">
              <span className="inline-flex items-center px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-accent/10 text-accent text-xs md:text-sm font-medium">
                <Icon name="Zap" size={14} className="mr-1.5 md:mr-2" />
                {t("homepage.hero.badge")}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight">
              {t("homepage.hero.title")}
            </h1>

            <p className="text-lg md:text-xl text-gray-300 mb-6 md:mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              {t("homepage.hero.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start">
              <Button
                variant="default"
                size="lg"
                className="bg-cta hover:bg-cta/90 text-cta-foreground text-sm md:text-base"
                iconName="ArrowRight"
                iconPosition="right"
              >
                {t("homepage.hero.cta")}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white/20 text-white hover:bg-white/10 text-sm md:text-base"
                iconName="Play"
                iconPosition="left"
              >
                {t("homepage.hero.watchVideo")}
              </Button>
            </div>

            <div className="mt-8 md:mt-12 flex flex-wrap gap-4 md:gap-8 justify-center lg:justify-start text-xs md:text-sm text-gray-400">
              <div className="flex items-center">
                <Icon
                  name="Shield"
                  size={14}
                  className="mr-1.5 md:mr-2 text-accent"
                />
                {t("homepage.hero.stats.security")}
              </div>
              <div className="flex items-center">
                <Icon
                  name="Users"
                  size={14}
                  className="mr-1.5 md:mr-2 text-accent"
                />
                {t("homepage.hero.stats.transformations")}
              </div>
              <div className="flex items-center">
                <Icon
                  name="Award"
                  size={14}
                  className="mr-1.5 md:mr-2 text-accent"
                />
                {t("homepage.hero.stats.leaders")}
              </div>
            </div>
          </motion.div>

          {/* Right Technical Diagram - Animated DevOps Workflow */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative order-1 lg:order-2"
          >
            <div className="relative bg-white/5 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 lg:p-8 border border-white/10">
              <div className="text-center mb-4 md:mb-6">
                <h3 className="text-base md:text-lg font-semibold text-white mb-1 md:mb-2">
                  {t("homepage.hero.flow.title")}
                </h3>
                <p className="text-xs md:text-sm text-gray-400">
                  {t("homepage.hero.flow.subtitle")}
                </p>
              </div>

              {/* Animated DevOps Transformation Workflow */}
              <div className="space-y-4 md:space-y-6">
                {/* Current State - Legacy Process */}
                <motion.div
                  className="relative p-3 md:p-4 bg-red-500/10 rounded-lg border border-red-500/20"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <div className="flex items-center">
                      <motion.div
                        className="w-2.5 h-2.5 md:w-3 md:h-3 bg-red-500 rounded-full mr-2 md:mr-3"
                        animate={
                          shouldReduceMotion ? {} : { scale: [1, 1.2, 1] }
                        }
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      <span className="text-white text-xs md:text-sm font-medium">
                        {t("homepage.hero.flow.beforeState")}
                      </span>
                    </div>
                    <div className="text-red-400 text-xs">
                      {t("homepage.hero.flow.beforeVelocity")}
                    </div>
                  </div>

                  {/* Legacy workflow visualization */}
                  <div className="flex items-center justify-between mb-2 space-x-2">
                    <div className="flex-1 text-center">
                      <div className="w-full h-5 md:h-6 bg-red-500/20 rounded text-xs flex items-center justify-center text-red-300 mb-1">
                        Dev
                      </div>
                    </div>
                    <motion.div
                      className="w-6 md:w-8 h-px bg-red-500/40"
                      animate={
                        shouldReduceMotion ? {} : { opacity: [0.4, 1, 0.4] }
                      }
                      transition={{ duration: 3, repeat: Infinity }}
                    />
                    <div className="flex-1 text-center">
                      <div className="w-full h-5 md:h-6 bg-red-500/20 rounded text-xs flex items-center justify-center text-red-300 mb-1">
                        QA
                      </div>
                    </div>
                    <motion.div
                      className="w-6 md:w-8 h-px bg-red-500/40"
                      animate={
                        shouldReduceMotion ? {} : { opacity: [0.4, 1, 0.4] }
                      }
                      transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                    />
                    <div className="flex-1 text-center">
                      <div className="w-full h-5 md:h-6 bg-red-500/20 rounded text-xs flex items-center justify-center text-red-300 mb-1">
                        Ops
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-red-400 text-center">
                    Siloed teams • Manual processes • Long lead times
                  </div>
                </motion.div>

                {/* SIGNA Transformation Arrow */}
                <motion.div
                  className="flex items-center justify-center py-3 md:py-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.0 }}
                >
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <motion.div
                      className="w-6 h-6 md:w-8 md:h-8 bg-accent/20 rounded-full flex items-center justify-center"
                      animate={shouldReduceMotion ? {} : { rotate: 360 }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <Icon
                        name="Zap"
                        size={12}
                        className="text-accent md:w-4 md:h-4"
                      />
                    </motion.div>
                    <div className="text-center">
                      <div className="text-accent text-xs md:text-sm font-medium">
                        SIGNA Transformation
                      </div>
                      <div className="text-xs text-gray-400 hidden md:block">
                        DevOps • Cloud • Automation
                      </div>
                    </div>
                    <motion.div
                      className="w-6 h-6 md:w-8 md:h-8 bg-accent/20 rounded-full flex items-center justify-center"
                      animate={shouldReduceMotion ? {} : { rotate: -360 }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <Icon
                        name="Settings"
                        size={12}
                        className="text-accent md:w-4 md:h-4"
                      />
                    </motion.div>
                  </div>
                </motion.div>

                {/* Modern CI/CD Pipeline */}
                <motion.div
                  className="relative p-3 md:p-4 bg-accent/10 rounded-lg border border-accent/20"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2 }}
                >
                  <div className="text-center mb-3 md:mb-4">
                    <div className="text-accent text-xs md:text-sm font-medium mb-1">
                      Automated CI/CD Pipeline
                    </div>
                    <div className="text-xs text-gray-400 hidden md:block">
                      Continuous Integration • Continuous Deployment
                    </div>
                  </div>

                  {/* Pipeline stages with sequential animation */}
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2 md:mb-3">
                      {/* Code Stage */}
                      <motion.div
                        className="flex flex-col items-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.4 }}
                      >
                        <motion.div
                          className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center mb-1 md:mb-2 relative transition-all duration-300 ${
                            activeStage >= 0 && isAnimating
                              ? "bg-accent/40 ring-1 md:ring-2 ring-accent/60"
                              : "bg-accent/20"
                          }`}
                          animate={
                            shouldReduceMotion
                              ? {}
                              : activeStage >= 0 && isAnimating
                                ? { scale: 1.2 }
                                : { scale: 1 }
                          }
                          transition={{ duration: 0.6 }}
                        >
                          <Icon
                            name="GitBranch"
                            size={12}
                            className={`md:w-[16px] md:h-[16px] transition-colors duration-300 ${
                              activeStage >= 0 && isAnimating
                                ? "text-accent"
                                : "text-accent/70"
                            }`}
                          />
                          {/* Progress indicator */}
                          {activeStage >= 0 && isAnimating && (
                            <motion.div
                              className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-2 h-2 md:w-3 md:h-3 bg-accent rounded-full"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.3 }}
                            />
                          )}
                        </motion.div>
                        <div
                          className={`text-xs font-medium transition-colors duration-300 ${
                            activeStage >= 0 && isAnimating
                              ? "text-accent"
                              : "text-gray-300"
                          }`}
                        >
                          Code
                        </div>
                      </motion.div>

                      {/* Connection 1 */}
                      <motion.div
                        className="flex-1 h-px bg-gradient-to-r from-accent/30 to-accent/60 mx-1 md:mx-2 relative"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 1.5, duration: 0.8 }}
                      >
                        <motion.div
                          className="absolute top-0 left-0 w-2 md:w-3 h-px bg-accent rounded-full"
                          initial={{ left: "0%" }}
                          animate={
                            shouldReduceMotion
                              ? {}
                              : activeStage >= 1 && isAnimating
                                ? { left: "95%" }
                                : { left: "0%" }
                          }
                          transition={{
                            duration: 0.8,
                            ease: "easeInOut",
                          }}
                        />
                        {/* Flow particles */}
                        {activeStage >= 1 &&
                          isAnimating &&
                          !shouldReduceMotion && (
                            <>
                              <motion.div
                                className="absolute top-0 left-0 w-1 h-px bg-accent/80 rounded-full"
                                initial={{ left: "0%" }}
                                animate={{ left: "95%" }}
                                transition={{
                                  duration: 0.6,
                                  ease: "easeInOut",
                                  delay: 0.1,
                                }}
                              />
                              <motion.div
                                className="absolute top-0 left-0 w-1 h-px bg-accent/60 rounded-full"
                                initial={{ left: "0%" }}
                                animate={{ left: "95%" }}
                                transition={{
                                  duration: 0.6,
                                  ease: "easeInOut",
                                  delay: 0.2,
                                }}
                              />
                            </>
                          )}
                      </motion.div>

                      {/* Build Stage */}
                      <motion.div
                        className="flex flex-col items-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.6 }}
                      >
                        <motion.div
                          className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center mb-1 md:mb-2 relative transition-all duration-300 ${
                            activeStage >= 1 && isAnimating
                              ? "bg-accent/40 ring-1 md:ring-2 ring-accent/60"
                              : "bg-accent/20"
                          }`}
                          animate={
                            shouldReduceMotion
                              ? {}
                              : activeStage >= 1 && isAnimating
                                ? { scale: 1.2 }
                                : { scale: 1 }
                          }
                          transition={{
                            duration: 0.6,
                            delay: activeStage >= 1 ? 0.8 : 0,
                          }}
                        >
                          <Icon
                            name="Package"
                            size={12}
                            className={`md:w-[16px] md:h-[16px] transition-colors duration-300 ${
                              activeStage >= 1 && isAnimating
                                ? "text-accent"
                                : "text-accent/70"
                            }`}
                          />
                          {activeStage >= 1 && isAnimating && (
                            <motion.div
                              className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-2 h-2 md:w-3 md:h-3 bg-accent rounded-full"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 1.1 }}
                            />
                          )}
                        </motion.div>
                        <div
                          className={`text-xs font-medium transition-colors duration-300 ${
                            activeStage >= 1 && isAnimating
                              ? "text-accent"
                              : "text-gray-300"
                          }`}
                        >
                          Build
                        </div>
                      </motion.div>

                      {/* Connection 2 */}
                      <motion.div
                        className="flex-1 h-px bg-gradient-to-r from-accent/30 to-accent/60 mx-1 md:mx-2 relative"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 1.7, duration: 0.8 }}
                      >
                        <motion.div
                          className="absolute top-0 left-0 w-2 md:w-3 h-px bg-accent rounded-full"
                          initial={{ left: "0%" }}
                          animate={
                            shouldReduceMotion
                              ? {}
                              : activeStage >= 2 && isAnimating
                                ? { left: "95%" }
                                : { left: "0%" }
                          }
                          transition={{
                            duration: 0.8,
                            ease: "easeInOut",
                            delay: activeStage >= 2 ? 0.2 : 0,
                          }}
                        />
                        {activeStage >= 2 &&
                          isAnimating &&
                          !shouldReduceMotion && (
                            <>
                              <motion.div
                                className="absolute top-0 left-0 w-1 h-px bg-accent/80 rounded-full"
                                initial={{ left: "0%" }}
                                animate={{ left: "95%" }}
                                transition={{
                                  duration: 0.6,
                                  ease: "easeInOut",
                                  delay: 0.3,
                                }}
                              />
                              <motion.div
                                className="absolute top-0 left-0 w-1 h-px bg-accent/60 rounded-full"
                                initial={{ left: "0%" }}
                                animate={{ left: "95%" }}
                                transition={{
                                  duration: 0.6,
                                  ease: "easeInOut",
                                  delay: 0.4,
                                }}
                              />
                            </>
                          )}
                      </motion.div>

                      {/* Test Stage */}
                      <motion.div
                        className="flex flex-col items-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.8 }}
                      >
                        <motion.div
                          className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center mb-1 md:mb-2 relative transition-all duration-300 ${
                            activeStage >= 2 && isAnimating
                              ? "bg-accent/40 ring-1 md:ring-2 ring-accent/60"
                              : "bg-accent/20"
                          }`}
                          animate={
                            shouldReduceMotion
                              ? {}
                              : activeStage >= 2 && isAnimating
                                ? { scale: 1.2 }
                                : { scale: 1 }
                          }
                          transition={{
                            duration: 0.6,
                            delay: activeStage >= 2 ? 0.8 : 0,
                          }}
                        >
                          <Icon
                            name="CheckCircle"
                            size={12}
                            className={`md:w-[16px] md:h-[16px] transition-colors duration-300 ${
                              activeStage >= 2 && isAnimating
                                ? "text-accent"
                                : "text-accent/70"
                            }`}
                          />
                          {activeStage >= 2 && isAnimating && (
                            <motion.div
                              className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-2 h-2 md:w-3 md:h-3 bg-accent rounded-full"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 1.1 }}
                            />
                          )}
                        </motion.div>
                        <div
                          className={`text-xs font-medium transition-colors duration-300 ${
                            activeStage >= 2 && isAnimating
                              ? "text-accent"
                              : "text-gray-300"
                          }`}
                        >
                          Test
                        </div>
                      </motion.div>

                      {/* Connection 3 */}
                      <motion.div
                        className="flex-1 h-px bg-gradient-to-r from-accent/30 to-success/60 mx-1 md:mx-2 relative"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 1.9, duration: 0.8 }}
                      >
                        <motion.div
                          className="absolute top-0 left-0 w-2 md:w-3 h-px bg-success rounded-full"
                          initial={{ left: "0%" }}
                          animate={
                            shouldReduceMotion
                              ? {}
                              : activeStage >= 3 && isAnimating
                                ? { left: "95%" }
                                : { left: "0%" }
                          }
                          transition={{
                            duration: 0.8,
                            ease: "easeInOut",
                            delay: activeStage >= 3 ? 0.2 : 0,
                          }}
                        />
                        {activeStage >= 3 &&
                          isAnimating &&
                          !shouldReduceMotion && (
                            <>
                              <motion.div
                                className="absolute top-0 left-0 w-1 h-px bg-success/80 rounded-full"
                                initial={{ left: "0%" }}
                                animate={{ left: "95%" }}
                                transition={{
                                  duration: 0.6,
                                  ease: "easeInOut",
                                  delay: 0.3,
                                }}
                              />
                              <motion.div
                                className="absolute top-0 left-0 w-1 h-px bg-success/60 rounded-full"
                                initial={{ left: "0%" }}
                                animate={{ left: "95%" }}
                                transition={{
                                  duration: 0.6,
                                  ease: "easeInOut",
                                  delay: 0.4,
                                }}
                              />
                            </>
                          )}
                      </motion.div>

                      {/* Deploy Stage */}
                      <motion.div
                        className="flex flex-col items-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 2.0 }}
                      >
                        <motion.div
                          className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center mb-1 md:mb-2 relative transition-all duration-300 ${
                            activeStage >= 3 && isAnimating
                              ? "bg-success/40 ring-1 md:ring-2 ring-success/60"
                              : "bg-success/20"
                          }`}
                          animate={
                            shouldReduceMotion
                              ? {}
                              : activeStage >= 3 && isAnimating
                                ? { scale: 1.2 }
                                : { scale: 1 }
                          }
                          transition={{
                            duration: 0.6,
                            delay: activeStage >= 3 ? 0.8 : 0,
                          }}
                        >
                          <Icon
                            name="Rocket"
                            size={12}
                            className={`md:w-[16px] md:h-[16px] transition-colors duration-300 ${
                              activeStage >= 3 && isAnimating
                                ? "text-success"
                                : "text-success/70"
                            }`}
                          />
                          {activeStage >= 3 && isAnimating && (
                            <motion.div
                              className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-2 h-2 md:w-3 md:h-3 bg-success rounded-full"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 1.1 }}
                            />
                          )}
                        </motion.div>
                        <div
                          className={`text-xs font-medium transition-colors duration-300 ${
                            activeStage >= 3 && isAnimating
                              ? "text-success"
                              : "text-gray-300"
                          }`}
                        >
                          Deploy
                        </div>
                      </motion.div>
                    </div>

                    {/* Stage Progress Bar */}
                    <motion.div
                      className="mt-3 md:mt-4 mb-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 2.2 }}
                    >
                      <div className="flex justify-center items-center space-x-2">
                        <div className="text-xs text-gray-400">Progress:</div>
                        <div className="flex space-x-1">
                          {[0, 1, 2, 3].map((stage) => (
                            <motion.div
                              key={stage}
                              className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full transition-all duration-300 ${
                                activeStage >= stage && isAnimating
                                  ? stage === 3
                                    ? "bg-success scale-110"
                                    : "bg-accent scale-110"
                                  : "bg-gray-600"
                              }`}
                              animate={
                                shouldReduceMotion
                                  ? {}
                                  : activeStage >= stage && isAnimating
                                    ? { scale: 1.3 }
                                    : { scale: 1 }
                              }
                              transition={{ duration: 0.3, delay: stage * 0.1 }}
                            />
                          ))}
                        </div>
                      </div>
                    </motion.div>

                    {/* Continuous feedback loop */}
                    <motion.div
                      className="mt-2 flex items-center justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 2.5 }}
                    >
                      <div className="flex items-center space-x-2">
                        <motion.div
                          className="w-2 h-2 bg-accent/60 rounded-full"
                          animate={shouldReduceMotion ? {} : { scale: 1.5 }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "reverse",
                          }}
                        />
                        <div className="text-xs text-gray-400">
                          Continuous Feedback Loop
                        </div>
                        <motion.div
                          className="w-2 h-2 bg-accent/60 rounded-full"
                          animate={shouldReduceMotion ? {} : { scale: 1.5 }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "reverse",
                            delay: 1,
                          }}
                        />
                      </div>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Transformed State - Modern DevOps */}
                <motion.div
                  className="relative p-3 md:p-4 bg-green-500/10 rounded-lg border border-green-500/20"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 2.2 }}
                >
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <div className="flex items-center">
                      <motion.div
                        className="w-2.5 h-2.5 md:w-3 md:h-3 bg-green-500 rounded-full mr-2 md:mr-3"
                        animate={shouldReduceMotion ? {} : { scale: 1.2 }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          repeatType: "reverse",
                        }}
                      />
                      <span className="text-white text-xs md:text-sm font-medium">
                        {t("homepage.hero.flow.afterState")}
                      </span>
                    </div>
                    <div className="text-green-400 text-xs font-medium">
                      {t("homepage.hero.flow.afterVelocity")}
                    </div>
                  </div>

                  {/* Modern workflow visualization */}
                  <div className="flex items-center justify-between mb-2 space-x-2">
                    <div className="flex-1 text-center">
                      <div className="w-full h-5 md:h-6 bg-green-500/20 rounded text-xs flex items-center justify-center text-green-300 mb-1">
                        DevOps
                      </div>
                    </div>
                    <motion.div
                      className="w-6 md:w-8 h-px bg-green-500/60"
                      animate={
                        shouldReduceMotion ? {} : { opacity: [0.6, 1, 0.6] }
                      }
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                    <div className="flex-1 text-center">
                      <div className="w-full h-5 md:h-6 bg-green-500/20 rounded text-xs flex items-center justify-center text-green-300 mb-1">
                        Cloud
                      </div>
                    </div>
                    <motion.div
                      className="w-6 md:w-8 h-px bg-green-500/60"
                      animate={
                        shouldReduceMotion ? {} : { opacity: [0.6, 1, 0.6] }
                      }
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        delay: 0.5,
                      }}
                    />
                    <div className="flex-1 text-center">
                      <div className="w-full h-5 md:h-6 bg-green-500/20 rounded text-xs flex items-center justify-center text-green-300 mb-1">
                        Auto
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-green-400 text-center">
                    Unified teams • Automated processes • Fast delivery
                  </div>
                </motion.div>
              </div>

              {/* Impact Metrics with Animation */}
              <div className="mt-6 md:mt-8 grid grid-cols-3 gap-3 md:gap-4 text-center">
                <motion.div
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.4 }}
                >
                  <motion.div
                    className="text-xl md:text-2xl font-bold text-accent mb-1"
                    animate={shouldReduceMotion ? {} : { scale: 1.05 }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  >
                    10x
                  </motion.div>
                  <div className="text-xs text-gray-400">
                    {t("homepage.hero.flow.metrics.faster")}
                  </div>
                </motion.div>
                <motion.div
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.5 }}
                >
                  <motion.div
                    className="text-xl md:text-2xl font-bold text-accent mb-1"
                    animate={shouldReduceMotion ? {} : { scale: 1.05 }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: 0.5,
                    }}
                  >
                    90%
                  </motion.div>
                  <div className="text-xs text-gray-400">
                    {t("homepage.hero.flow.metrics.debt")}
                  </div>
                </motion.div>
                <motion.div
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.6 }}
                >
                  <motion.div
                    className="text-xl md:text-2xl font-bold text-accent mb-1"
                    animate={shouldReduceMotion ? {} : { scale: 1.05 }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: 1,
                    }}
                  >
                    50+
                  </motion.div>
                  <div className="text-xs text-gray-400">
                    {t("homepage.hero.flow.metrics.enterprises")}
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
